import { NavigationItem } from '../../../shared/components/layout/navigationItems';
import { SvgIconProps } from '@mui/material';

/**
 * Searchable navigation item with parent context
 */
export interface SearchableNavItem {
  text: string;
  parent?: string;
  path: string;
  icon: React.ComponentType<SvgIconProps>;
  permission?: string;
  keywords?: string[];
}

/**
 * Flatten navigation items into searchable list with parent context
 */
export function flattenNavItems(
  items: NavigationItem[],
  parent?: string
): SearchableNavItem[] {
  const flattened: SearchableNavItem[] = [];

  for (const item of items) {
    // Add the parent item itself
    flattened.push({
      text: item.text,
      parent,
      path: item.path,
      icon: item.icon,
      permission: item.permission,
      keywords: generateKeywords(item.text),
    });

    // Recursively add sub-items with parent context
    if (item.subItems && item.subItems.length > 0) {
      const subItemsFlattened = flattenNavItems(item.subItems, item.text);
      flattened.push(...subItemsFlattened);
    }
  }

  return flattened;
}

/**
 * Generate search keywords for better matching
 * e.g., "Labour Items" → ["labour", "items", "labor", "work"]
 */
function generateKeywords(text: string): string[] {
  const keywords: string[] = [];
  const lowerText = text.toLowerCase();

  // Add the original words
  keywords.push(...lowerText.split(' '));

  // Add common aliases
  const aliases: Record<string, string[]> = {
    sites: ['projects', 'project', 'site'],
    labour: ['labor', 'work'],
    gangs: ['teams', 'groups', 'crew'],
    approvals: ['approve', 'review'],
    audit: ['history', 'log', 'events'],
    settings: ['preferences', 'config'],
  };

  for (const [key, values] of Object.entries(aliases)) {
    if (lowerText.includes(key)) {
      keywords.push(...values);
    }
  }

  return keywords;
}

/**
 * Search navigation items by query string
 * Returns items where text or keywords match the query
 */
export function searchNavItems(
  query: string,
  items: SearchableNavItem[]
): SearchableNavItem[] {
  if (!query || query.trim().length === 0) {
    return [];
  }

  const lowerQuery = query.toLowerCase().trim();

  return items.filter(item => {
    const textMatch = item.text.toLowerCase().includes(lowerQuery);
    const keywordMatch = item.keywords?.some(keyword =>
      keyword.includes(lowerQuery)
    );
    const parentMatch = item.parent?.toLowerCase().includes(lowerQuery);

    return textMatch || keywordMatch || parentMatch;
  });
}

/**
 * Filter navigation items by user permissions
 * Only returns items the user has permission to access
 */
export function filterByPermissions(
  items: SearchableNavItem[],
  userPermissions: string[]
): SearchableNavItem[] {
  return items.filter(item => {
    // If no permission required, item is always visible
    if (!item.permission) {
      return true;
    }

    // Check if user has the required permission
    return userPermissions.includes(item.permission);
  });
}

/**
 * Remove duplicate navigation items (e.g., parent appears in both main and sub-items)
 */
export function deduplicateNavItems(
  items: SearchableNavItem[]
): SearchableNavItem[] {
  const seen = new Set<string>();
  const deduplicated: SearchableNavItem[] = [];

  for (const item of items) {
    // Create unique key from path
    const key = item.path;

    if (!seen.has(key)) {
      seen.add(key);
      deduplicated.push(item);
    }
  }

  return deduplicated;
}

/**
 * Filter out parent items when their children are present in results
 * E.g., if "Users" is in results, hide "Workforce"
 */
export function filterOutParentItems(
  items: SearchableNavItem[]
): SearchableNavItem[] {
  // Create a set of all parent names in the results
  const parentNames = new Set(items.map(item => item.parent).filter(Boolean));

  // Filter out items whose text matches a parent name in the set
  return items.filter(item => {
    // If this item's text is a parent of another item in results, exclude it
    return !parentNames.has(item.text);
  });
}
